import Link from "next/link";
import { sanitizeHTML } from "@/lib/utils";

interface ProcedureCardsData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  procedure_cards?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title: string;
          text: string;

          href: string;
          label: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",

  "text.text",

  "procedure_cards.id",
  "procedure_cards.site_content_collections_id.collection.collection",
  "procedure_cards.site_content_collections_id.collection.item:site_content.title",
  "procedure_cards.site_content_collections_id.collection.item:site_content.text",
  "procedure_cards.site_content_collections_id.collection.item:menu_items.href",
  "procedure_cards.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface ProcedureCardsProps {
  data: ProcedureCardsData;
}

export default function ProcedureCards({ data }: ProcedureCardsProps) {
  return (
    <section className="bg-white">
      <div className="container mx-auto py-8 xl:py-16 px-8 xl:px-16">
        <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-14">
          {data.heading?.text || data.heading?.title || ""}
        </h2>
        {data.text?.text && (
          <p
            className="text-[1rem] font-normal leading-normal font-greycliff text-primary tracking-tight mb-8 md:text-lg md:leading-relaxed max-w-4xl"
            dangerouslySetInnerHTML={sanitizeHTML(data.text.text || "")}
          />
        )}
        <div className="flex gap-4 overflow-x-auto bader-h-scrollbar pb-8 lg:pb-0 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(250px,1fr))]">
          {data.procedure_cards?.map((card, index) => {
            const titleItem = card.site_content_collections_id?.collection.find(
              (item) => item.collection === "site_content" && item.item.title
            )?.item;
            const textItem = card.site_content_collections_id?.collection.find(
              (item) => item.collection === "site_content" && item.item.text
            )?.item;
            const menuItem = card.site_content_collections_id?.collection.find(
              (item) => item.collection === "menu_items"
            )?.item;
            const title = titleItem?.title;
            const text = textItem?.text;
            const buttonLabel = menuItem?.label;
            const buttonHref = menuItem?.href;

            return (
              <div
                key={index}
                className="flex-none w-[20rem] md:w-auto flex flex-col justify-between p-6 rounded-[0.625rem] border border-primary min-h-[35.5rem] transition-all duration-300 hover:bg-light"
              >
                <div className="flex flex-col gap-5">
                  <h3
                    className="text-[1.75rem] font-medium font-greycliff text-primary leading-[2.38rem] tracking-tight"
                    dangerouslySetInnerHTML={sanitizeHTML(title || "")}
                  />
                  <div
                    className="[&>p]:text-[1rem] text-[1rem] font-normal leading-normal font-greycliff text-primary tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:leading-relaxed md:[&>*]:text-lg md:[&>*]:leading-relaxed"
                    dangerouslySetInnerHTML={sanitizeHTML(text || "")}
                  />
                </div>
                {buttonHref && (
                  <Link
                    href={buttonHref}
                    className="py-3.5 border-t border-b border-primary w-full text-center block"
                  >
                    <span className="text-xl font-medium font-greycliff text-primary leading-7 tracking-tight flex flex-col">
                      <span>
                        {buttonLabel
                          ?.split(" ")
                          .slice(0, Math.ceil(buttonLabel?.split(" ").length / 2))
                          .join(" ")}
                      </span>
                      <span>
                        {buttonLabel
                          ?.split(" ")
                          .slice(Math.ceil(buttonLabel?.split(" ").length / 2))
                          .join(" ")}
                      </span>
                    </span>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
